import { useAxios } from "auth-sdk";
import { addAction } from "@wayflyer/blackbox-fe";
import { Flex, FlyError, Link, ResourceList, Text, TextContainer } from "@wayflyer/flyui";
import { handleContactTeam } from "shared-utils";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useErrorModal } from "shared-ui";

import { MESSAGES } from "./message-definitions";

export interface ReviewScreenEmailVerificationProps {
  emailAddress: string;
}

export const ReviewScreenEmailVerification = ({ emailAddress }: ReviewScreenEmailVerificationProps) => {
  const intl = useIntl();
  const { post } = useAxios();
  const { openErrorModal } = useErrorModal();
  const [emailVerifyRequestSent, setEmailVerifyRequestSent] = useState(false);

  const sendEmailVerificationRequest = () => {
    try {
      post("/company-creation/api/email-verification/send");
      addAction("email verification request sent");
      setEmailVerifyRequestSent(true);
      setTimeout(() => {
        setEmailVerifyRequestSent(false);
      }, 30000);
    } catch {
      openErrorModal(
        new FlyError({
          title: intl.formatMessage(MESSAGES.emailVerificationFailureTitle),
          message: (
            <FormattedMessage
              id="funding-application: verification email request failed message"
              defaultMessage="<p>We were unable to send the verification email. If the problem persists please <a>contact our support team</a>.</p>"
              values={{
                p: (chunks: string) => <Text>{chunks}</Text>,
                a: (chunks: string) => <Link onClick={handleContactTeam}>{chunks}</Link>,
              }}
            />
          ),
          error: new Error("Email verification error"),
        })
      );
    }
  };

  return (
    <Flex direction="column" gap="16">
      <TextContainer>
        <FormattedMessage
          id="funding-application: email verification details"
          defaultMessage="<p>To ensure the security of your account, we've sent a verification link to your registered email address. Please check your inbox and click on the link to complete the verification process.</p><p>Email verification is required to progress with your application.</p>"
          values={{
            p: (chunks: string) => <Text>{chunks}</Text>,
          }}
        />
      </TextContainer>
      <ResourceList.InlineActionItem
        label={intl.formatMessage(MESSAGES.emailVerificationStepTitle)}
        action={{
          responsiveFullWidth: true,
          onClick: sendEmailVerificationRequest,
          label: intl.formatMessage(
            emailVerifyRequestSent
              ? MESSAGES.emailVerificationStepButtonSent
              : MESSAGES.emailVerificationStepButtonReSend
          ),
          disabled: emailVerifyRequestSent,
        }}
      >
        <FormattedMessage
          id="funding-application: verification email request hint"
          defaultMessage="<p>Please verify the email address you signed up with to continue: <b>{emailAddress}</b></p>"
          values={{
            p: (chunks: string) => <Text size="sm">{chunks}</Text>,
            b: (chunks: string) => <strong>{chunks}</strong>,
            emailAddress: emailAddress,
          }}
        />
      </ResourceList.InlineActionItem>
    </Flex>
  );
};
